/**
 * <ritual-product-card>
 *
 * Wiederverwendbare Produktkarte für Ritual-Artikel. Lädt Live-Produktdaten
 * clientseitig über Shopifys öffentlichen /products/{handle}.js-Endpunkt und
 * rendert eine klickbare Karte direkt an der Stelle im Artikeltext, an der sie
 * im HTML steht — dadurch lassen sich Produkte mitten im Fließtext platzieren
 * (z. B. in der Materialliste), was mit Theme-Sections/Blocks nicht möglich
 * ist, weil die nur vor/nach dem gesamten Artikel-Content stehen können.
 *
 * Zeigt bewusst keinen Preis (Entscheidung DIY SPIRIT, 04.09.2026) — nur Bild,
 * Name und einen Link zur aktuellen Produktseite ("Jetzt entdecken →" bei
 * variant="tile", "{Produktname} entdecken →" bei variant="feature" — Letzteres
 * folgt bewusst dem Linktext-Muster der bestehenden Empfehlungsboxen).
 *
 * Solange die Produktdaten noch laden, zeigt das Element einen Platzhalter in
 * exakt der Zielgröße der Kachel/Box (gleiche CSS-Klassen wie das fertige
 * Ergebnis) — so hat es nie eine Fläche von 0×0 und "verschwindet" nicht
 * kurzzeitig beim Laden (Fix 04.09.2026, siehe ritual-produktfokus-template-konzept.md).
 *
 * Verwendung im Artikel-HTML:
 *
 *   <ritual-product-card handle="rauchquarz-set" variant="tile"></ritual-product-card>
 *   <ritual-product-card handle="raeucherstaebchen-salbei" variant="tile" size="sm"
 *     role-label="für die Reinigung"></ritual-product-card>
 *   <ritual-product-card handle="aufbewahrungsbeutel-samt" variant="feature"
 *     heading="Damit die Wirkung bleibt"
 *     text="Bewahre deinen Stein danach achtsam auf, damit er dich auch in den
 *     nächsten Tagen begleiten kann."></ritual-product-card>
 *
 * Attribute:
 *   handle       (Pflicht) Produkt-Handle, z. B. "rauchquarz-set"
 *   variant      "tile" (Standard) | "feature"
 *   size         nur bei variant="tile": "lg" (Standard, Hero-Kachel) | "sm" (Materialkarte)
 *   role-label   optionale kurze Rollenbezeichnung unter dem Namen (nur "tile")
 *   cta          CTA-Text, Standard "Jetzt entdecken →"
 *   heading      nur bei variant="feature": Überschrift der Box
 *   text         nur bei variant="feature": erklärender Text neben dem Bild
 */
(function () {
  if (customElements.get('ritual-product-card')) return;

  var STYLE_ID = 'ritual-product-card-styles';
  // Die "feature"-Variante (Touchpoint 3, Nachwirkungs-Box) folgt bewusst exakt
  // dem bestehenden "Unsere Empfehlung"-/"Dein [Thema]-Begleiter"-Boxenstil, der
  // in anderen Artikeln bereits verwendet wird: linker Akzentrand statt Vollrahmen,
  // keine Eckenrundung auf der Box, Bild links float ohne Rahmen, Überschrift/Text/
  // Link erben die normale Theme-Typografie statt eigener Schriftgrößen (Fix 04.09.2026).
  var CSS = [
    'ritual-product-card{display:block}',
    '.rpc-tile{display:flex;flex-direction:column;align-items:flex-start;',
    'text-decoration:none;color:inherit;flex:1 1 140px;max-width:170px}',
    '.rpc-tile.rpc-sm{flex-basis:120px;max-width:140px}',
    '.rpc-tile__img-wrap{width:100%;aspect-ratio:1/1;border-radius:var(--media-radius,20px);',
    'overflow:hidden;background:#f1ece1;margin-bottom:8px}',
    '.rpc-tile__img{width:100%;height:100%;object-fit:cover;display:block;',
    'transition:transform .25s ease}',
    '.rpc-tile:hover .rpc-tile__img{transform:scale(1.04)}',
    '.rpc-tile__name{font-weight:700;font-size:1.45rem;line-height:1.3;color:#2b2620;',
    'margin:0 0 2px}',
    '.rpc-tile.rpc-sm .rpc-tile__name{font-size:1.3rem}',
    '.rpc-tile__role{font-size:1.2rem;color:#8a7f6e;margin:0 0 4px;font-style:italic}',
    '.rpc-tile__cta{font-size:1.25rem;font-weight:600;color:#a33a32;white-space:nowrap}',
    '.rpc-tile.rpc-sm .rpc-tile__cta{font-size:1.2rem}',
    '.rpc-tile__badge{font-size:1.15rem;color:#8a7f6e;margin-left:4px}',
    '.rpc-feature{display:block;background:#fcfaf5;border-left:3px solid #a33a32;',
    'padding:22px 24px;margin:28px 0}',
    '.rpc-feature::after{content:"";display:block;clear:both}',
    '.rpc-feature__img{width:110px;height:110px;object-fit:cover;border-radius:12px;',
    'float:left;margin:0 20px 12px 0;display:block;background:#f1ece1}',
    '.rpc-feature__heading{margin:0 0 10px}',
    '.rpc-feature__text{margin:0 0 12px}',
    '.rpc-feature__linkline{margin:0}',
    '.rpc-feature__cta{font-weight:600;text-decoration:underline}',
    '.rpc-skeleton{cursor:default;pointer-events:none}',
    '.rpc-skeleton .rpc-tile__img-wrap,.rpc-skeleton .rpc-feature__img{',
    'animation:rpc-pulse 1.4s ease-in-out infinite}',
    '.rpc-skel-line{display:block;height:.75em;border-radius:4px;background:#efe9dc;',
    'margin-top:8px;animation:rpc-pulse 1.4s ease-in-out infinite}',
    '@keyframes rpc-pulse{0%,100%{opacity:1}50%{opacity:.5}}',
    '@media (max-width:480px){.rpc-feature{padding:16px}}'
  ].join('');

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // Ein gemeinsamer Cache pro Seitenaufruf, falls dasselbe Handle mehrfach
  // im Artikel vorkommt.
  var productCache = window.__ritualProductCache || (window.__ritualProductCache = {});

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Produkt nicht gefunden: ' + handle);
      return res.json();
    });
    productCache[handle] = promise;
    return promise;
  }

  function resizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function RitualProductCard() {
    return Reflect.construct(HTMLElement, [], RitualProductCard);
  }
  Object.setPrototypeOf(RitualProductCard.prototype, HTMLElement.prototype);
  Object.setPrototypeOf(RitualProductCard, HTMLElement);

  RitualProductCard.prototype.connectedCallback = function () {
    ensureStyles();
    var handle = this.getAttribute('handle');
    if (!handle) {
      this.remove();
      return;
    }
    this.renderSkeleton();
    var self = this;
    fetchProduct(handle)
      .then(function (product) {
        self.render(product);
      })
      .catch(function () {
        // Produkt nicht (mehr) verfügbar/aktiv – Element still entfernen,
        // damit kein kaputter Link im Artikel stehen bleibt.
        self.remove();
      });
  };

  // Platzhalter in derselben Struktur/Größe wie das fertige Ergebnis, damit
  // die Kachel während des Ladens nie eine Fläche von 0×0 hat.
  RitualProductCard.prototype.renderSkeleton = function () {
    var variant = this.getAttribute('variant') || 'tile';

    if (variant === 'feature') {
      this.innerHTML =
        '<div class="rpc-feature rpc-skeleton">' +
          '<span class="rpc-feature__img"></span>' +
          '<span class="rpc-skel-line" style="width:55%;height:1.1em;margin-top:0"></span>' +
          '<span class="rpc-skel-line" style="width:92%"></span>' +
          '<span class="rpc-skel-line" style="width:70%"></span>' +
          '<span class="rpc-skel-line" style="width:40%"></span>' +
        '</div>';
      return;
    }

    var size = this.getAttribute('size') === 'sm' ? 'rpc-sm' : '';
    this.innerHTML =
      '<span class="rpc-tile ' + size + ' rpc-skeleton">' +
        '<span class="rpc-tile__img-wrap"></span>' +
        '<span class="rpc-skel-line" style="width:82%;margin-top:0"></span>' +
        '<span class="rpc-skel-line" style="width:48%"></span>' +
      '</span>';
  };

  RitualProductCard.prototype.render = function (product) {
    var variant = this.getAttribute('variant') || 'tile';
    var cta = this.getAttribute('cta') || 'Jetzt entdecken →';
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var url = product.url;
    var name = product.title;
    var available = product.available !== false;

    if (variant === 'feature') {
      var heading = this.getAttribute('heading') || '';
      var text = this.getAttribute('text') || '';
      // Linktext folgt dem Muster der bestehenden Boxen: "{Produktname} entdecken →"
      // statt eines separaten CTA-Buttons.
      var linkLabel = escapeHtml(name) + ' ' + (available ? 'entdecken →' : 'ansehen →');
      this.innerHTML =
        '<div class="rpc-feature">' +
          (image ? '<img class="rpc-feature__img" src="' + escapeHtml(resizedImage(image, 220)) + '" alt="' + escapeHtml(name) + '" loading="lazy">' : '') +
          (heading ? '<h3 class="rpc-feature__heading">' + escapeHtml(heading) + '</h3>' : '') +
          (text ? '<p class="rpc-feature__text">' + escapeHtml(text) + '</p>' : '') +
          '<p class="rpc-feature__linkline"><a class="rpc-feature__cta" href="' + escapeHtml(url) + '">' + linkLabel + '</a></p>' +
        '</div>';
      return;
    }

    // variant === 'tile' (Standard)
    var size = this.getAttribute('size') === 'sm' ? 'rpc-sm' : '';
    var roleLabel = this.getAttribute('role-label') || '';
    var imgWidth = size === 'rpc-sm' ? 144 : 192;
    this.innerHTML =
      '<a class="rpc-tile ' + size + '" href="' + escapeHtml(url) + '">' +
        '<span class="rpc-tile__img-wrap">' +
          (image ? '<img class="rpc-tile__img" src="' + escapeHtml(resizedImage(image, imgWidth)) + '" alt="' + escapeHtml(name) + '" loading="lazy">' : '') +
        '</span>' +
        '<span class="rpc-tile__name">' + escapeHtml(name) + '</span>' +
        (roleLabel ? '<span class="rpc-tile__role">' + escapeHtml(roleLabel) + '</span>' : '') +
        '<span class="rpc-tile__cta">' + escapeHtml(available ? cta : 'Ansehen →') +
          (available ? '' : '<span class="rpc-tile__badge">· bald wieder da</span>') +
        '</span>' +
      '</a>';
  };

  customElements.define('ritual-product-card', RitualProductCard);
})();
